
import { mapActions, mapGetters, mapState } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mdiArrowRight, mdiFormatListBulletedType, mdiRvTruck, mdiTag, mdiCurrencyEur } from '@mdi/js'
import eav from '@/components/tools/eav'
import LoadingContainer from '@/components/ui/LoadingContainer.vue'
import utils from '@/components/tools/utils'
import constants from '@/components/tools/constants'
import tracking from '@/components/tools/tracking'

export default {
  icons: { mdiRvTruck, mdiFormatListBulletedType, mdiTag, mdiArrowRight, mdiCurrencyEur },
  components: {
    LoadingContainer,
    ValidationObserver,
    ValidationProvider
  },
  props: {
    section: { type: Object, required: true }
  },
  data () {
    return {
      loading: false
    }
  },
  async fetch () {
    await Promise.all([
      this.$store.dispatch('upload/getOnboardingStepperData'),
      this.$store.dispatch('upload/getAttributes')
    ])
  },
  computed: {
    ...mapState('upload', {
      product: state => state.product,
      attributes: state => state.attributes
    }),
    ...mapState('customer', ['merchant', 'hasCompletedProfileData', 'customer']),
    ...mapGetters({ isNlDomain: 'isNlDomain' }),
    isMerchant () {
      return typeof this.merchant?.merchantId === 'number'
    },
    make: {
      get () {
        return eav.getEavAttribute(
          'make',
          this.product.attributes,
          this.attributes
        )
      },
      set (value) {
        this.$store.commit('upload/setAttribute', {
          attributeCode: 'make',
          value
        })
        this.postGetConditionallyRequiredAttributes()
      }
    },
    category: {
      get () {
        return eav.getEavAttribute(
          'category',
          this.product.attributes,
          this.attributes
        )
      },
      set (value) {
        this.$store.commit('upload/setAttribute', {
          attributeCode: 'category',
          value
        })
      }
    },
    chassisSelect: {
      get () {
        return eav.getEavAttribute(
          'chassis_select',
          this.product.attributes,
          this.attributes
        )
      },
      set (value) {
        this.$store.commit('upload/setAttribute', {
          attributeCode: 'chassis_select',
          value
        })
        this.postGetConditionallyRequiredAttributes()
      }
    },

    targetPrice: {
      get () {
        return this.product.auction.targetPrice
      },
      set (value) {
        this.$store.dispatch('upload/setSimpleAuctionAttribute', {
          attributeCode: 'targetPrice',
          value
        })
        this.$store.dispatch('upload/setSimpleAuctionAttribute', {
          attributeCode: 'buyNowPrice',
          value
        })
      }
    },
    makeOptions () {
      return this.attributes.make?.options?.filter(
        option => !utils.isMakeBlacklisted(option.label)
      )
    },
    attributesLoaded () {
      return Boolean(this.attributes?.make && this.attributes?.category)
    },
    ctaText () {
      return this.$t('onboarding.cta.ad.else.new')
    },
    disabled () {
      return this.product?.auction?.status !== 'editing'
    }
  },
  mounted () {
    // the server-side fetch failed, so the attributes are missing — retry on the client
    if (this.$fetchState.error) {
      this.$fetch()
    }
  },
  methods: {
    ...mapActions('upload', ['postGetConditionallyRequiredAttributes']),
    roundTargetPrice (value) {
      if (value >= 1000) {
        this.targetPrice = Math.ceil(value / 100) * 100
      }
    },

    async submit () {
      this.loading = true

      if (this.disabled) {
        await this.justGoToMyAds()
      } else {
        await this.submitAndGoToAdIntake()
      }

      this.loading = false
    },
    async justGoToMyAds () {
      if (this.isMerchant) {
        await this.$router.push(
          this.localePath({
            name: 'account-merchant-ads'
          })
        )
      } else {
        await this.$router.push(
          this.localePath({
            name: 'account-my-vehicles'
          })
        )
      }
    },
    getSource () {
      if (this.source) {
        return this.source
      }
      if (this.$route.name.startsWith('index___')) {
        return constants.TP.SOURCE.MAIN_LP
      }
      if (this.$route.name.startsWith('sell')) {
        return constants.TP.SOURCE.SELL
      }
      return constants.TP.SOURCE.OTHER_LP
    },
    async submitAndGoToAdIntake () {
      if (this.product.entityId) {
        const { status, data } = await this.$store.dispatch(
          'upload/updateProduct',
          {
            entityId: this.product.entityId
          }
        )

        if (status !== 200) {
          await this.$store.dispatch('general/showSnackbar', {
            text: this.$t('error'),
            color: 'error'
          })
          return
        }

        const step = utils.getStepForAdInEditing(this.product, this.hasCompletedProfileData &&
          (this.customer.phoneNumberVerificationBypassedManually || this.customer.phoneNumberVerified),
        this.$auth.loggedIn)

        const productType = data.productType
        await this.$router.push(
          this.localePath({
            name: `ad-edit-${productType}-${step}-id`,
            params: { id: this.product.entityId }
          })
        )
      } else {
        const { status, data } = await this.$store.dispatch(
          'upload/createProduct',
          {
            source: tracking.getSource(this.source, this.$route),
            sourceUrl: this.$route.path,
            funnel: constants.TP.FUNNEL.AD_INTAKE
          }
        )
        if (status !== 200) {
          await this.$store.dispatch('general/showSnackbar', {
            text: this.$t('error'),
            color: 'error'
          })
          return
        }
        const productType = data.productType
        await this.$router.push(
          this.localePath({
            name: `ad-edit-${productType}-profile-id`,
            params: { id: data.entityId }
          })
        )
      }
    }

  }
}
